import { API_ORIGIN } from '../config';
const API_BASE_URL = `${API_ORIGIN}/api`;

class DirectOrderService {
  // Get all direct orders for the current user
  async getUserDirectOrders(userEmail) {
    try {
      if (!userEmail) {
        return {
          orders: [],
          message: 'No user email provided'
        };
      }

      const url = new URL(`${API_BASE_URL}/direct-orders/displayDirectOrders`);
      url.searchParams.append('userEmail', userEmail);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for cross-origin requests
      });

      if (!response.ok) {
        // Capture detailed error information
        const contentType = response.headers.get('content-type') || '';
        let errorBody = '';
        try {
          errorBody = await response.text();
        } catch (e) {
          errorBody = '<unable to read response body>';
        }

        let errorMessage = `HTTP ${response.status} ${response.statusText}`;
        if (contentType.includes('application/json') && errorBody) {
          try {
            const parsed = JSON.parse(errorBody);
            errorMessage += `: ${parsed.error || parsed.message || JSON.stringify(parsed)}`;
          } catch (e) {
            errorMessage += `: ${errorBody}`;
          }
        } else if (errorBody) {
          errorMessage += `: ${errorBody}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.success && data.directOrders) {
        return {
          orders: data.directOrders,
          message: 'Successfully loaded direct orders'
        };
      } else {
        return {
          orders: [],
          message: 'No direct orders found'
        };
      }

    } catch (error) {
      throw error;
    }
  }

  // Get all direct orders (for admin/approver view)
  async getAllDirectOrders() {
    try {
      const response = await fetch(`${API_BASE_URL}/direct-orders/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch all direct orders');
      }

      const data = await response.json();
      
      if (data.success && data.orders) {
        return { orders: data.orders };
      } else if (data.orders) {
        return { orders: data.orders };
      } else {
        return { orders: [] };
      }
    } catch (error) {
      throw error;
    }
  }

  // Direct orders the logged-in user is CC'd in. The backend picks them by the login, so this
  // works for every employee (the full /all list is for order admins only).
  async getCCDirectOrders(userEmail) {
    if (!userEmail) {
      return { orders: [], message: 'No user email provided' };
    }

    const response = await fetch(`${API_BASE_URL}/direct-orders/cc-orders`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch CC direct orders');
    }

    const data = await response.json();
    return { orders: Array.isArray(data.orders) ? data.orders : [], message: 'Successfully loaded CC direct orders' };
  }

  // Get eligible direct orders for ticket creation (all 3 approvals = 1, not deleted)
  async getEligibleDirectOrdersForTickets(userEmail) {
    try {
      if (!userEmail) {
        return {
          orders: [],
          message: 'No user email provided'
        };
      }

      const url = new URL(`${API_BASE_URL}/direct-order/tickets/eligible-dos`);
      url.searchParams.append('email', userEmail);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Failed to fetch eligible direct orders: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success && data.directOrders) {
        return {
          orders: data.directOrders,
          message: 'Successfully loaded eligible direct orders'
        };
      } else {
        return {
          orders: [],
          message: 'No eligible direct orders found'
        };
      }

    } catch (error) {
      throw error;
    }
  }


  // Create a new direct order
  async createDirectOrder(directOrderData, attachments = []) {
    try {
      const formData = new FormData();
      
      // Add all form fields to FormData
      Object.keys(directOrderData).forEach(key => {
        if (directOrderData[key] !== null && directOrderData[key] !== undefined) {
          formData.append(key, directOrderData[key]);
        }
      });
      
      // Add attachments if any
      if (attachments && attachments.length > 0) {
        attachments.forEach((file, index) => {
          formData.append(`attachment${index}`, file);
        });
      }

      // Lightweight FormData preview removed

      const response = await fetch(`${API_BASE_URL}/direct-orders/submit`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        // Try to capture raw response body and headers to diagnose 400 errors
        const contentType = response.headers.get('content-type') || '';
        let rawBody = '';
        try {
          rawBody = await response.text();
        } catch (e) {
          rawBody = '<unable to read response body>';
        }

        let errorMessage = `HTTP ${response.status} ${response.statusText}`;
        try {
          if (contentType.includes('application/json')) {
            const parsed = JSON.parse(rawBody);
            errorMessage += `: ${parsed.message || JSON.stringify(parsed)}`;
          } else {
            errorMessage += `: ${rawBody}`;
          }
        } catch (e) {
          errorMessage += `: ${rawBody}`;
        }

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Get direct order by ID
  async getDirectOrderById(doId) {
    try {
      const response = await fetch(`${API_BASE_URL}/direct-orders/${doId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch direct order');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Update direct order status (approve/decline)
  async updateDirectOrderStatus(doId, action, approvalLevel, comments = '', user = null) {
    try {
      // Determine the correct endpoint based on action
      const endpoint = action === 'approve' 
        ? `${API_BASE_URL}/direct-orders/approve/${doId}`
        : `${API_BASE_URL}/direct-orders/reject/${doId}`;
      
      const requestBody = {
        approvalLevel,
      };
      
      // Add the appropriate comment field based on action
      if (action === 'reject') {
        requestBody.declineReason = comments || '';
      } else {
        requestBody.comments = comments || '';
      }
      
      // Add required user information if available
      if (user) {
        requestBody.userEmail = user.email;
        
        // Try to get the numeric userId from user object
        // Check multiple possible properties for userId
        let userId = user.userId || user.id || user.user_id || user.ID;
        
        if (!userId) {
          throw new Error('User ID is required but not found in user object. Please ensure the login API returns a userId field.');
        }
        
        requestBody.userId = userId;
      } else {
        throw new Error('User information is required for approval actions');
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to ${action} direct order: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Check status of a direct order
  async checkStatus(doId) {
    try {
      const response = await fetch(`${API_BASE_URL}/direct-order/check-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          doId: doId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check direct order status');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Check status with date of a direct order
  async checkStatusWithDate(doId) {
    try {
      const response = await fetch(`${API_BASE_URL}/direct-order/check-status-with-date`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          doId: doId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check direct order status with date');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Update a direct order (only if rejected)
  async updateDirectOrder(orderId, directOrderData) {
    if (!orderId) throw new Error('No order ID provided');
    // Only allow update if the order is rejected
    // You should check this in the backend too, but here is a frontend check:

    try {
      const response = await fetch(`${API_BASE_URL}/direct-orders/update/${orderId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(directOrderData),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update direct order: ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Get current direct order reference number
  async getCurrentReference() {
    try {
      const response = await fetch(`${API_BASE_URL}/direct-order/reference`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to get current reference');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Update direct order reference number
  async updateReference(doReference) {
    try {
      const response = await fetch(`${API_BASE_URL}/direct-order/reference`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ doReference }),
      });

      if (!response.ok) {
        throw new Error('Failed to update reference');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Delete a direct order (admin only)
  async deleteDirectOrder(doId, userEmail) {
    try {
      const response = await fetch(`${API_BASE_URL}/direct-orders/${doId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ userEmail }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(result.error || 'Unauthorized: Only administrators can delete Direct Orders');
        } else if (response.status === 401) {
          throw new Error(result.message || 'Authentication required');
        }
        throw new Error(result.message || 'Failed to delete direct order');
      }

      return result;
    } catch (error) {
      throw error;
    }
  }
}

export const directOrderService = new DirectOrderService();
